import './String';
import { extend as extendString } from './String';
// Force implementation
extendString();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare global {
  interface Date {
    getWeek(): Number | number;
    toStringFormat(format?: String | string | undefined, dayNames?: Array<String | string> | undefined): String | string;
    toInputDateString(): String | string;
    toInputTimeString(): String | string;
    toInputMonthString(): String | string;
    toInputWeekString(): String | string;
    toInputDatetimeLocalString(): String | string;
    toInputString(type: 'date' | 'month' | 'week' | 'time' | 'datetime-local'): String | string;
  }

  interface DateConstructor {
    isDate(value: any): boolean;
    parseData(value: any): Date;
    toInputDateString(value: any): String | string;
    toInputTimeString(value: any): String | string;
    toInputMonthString(value: any): String | string;
    toInputWeekString(value: any): String | string;
    toInputDatetimeLocalString(value: any): String | string;
    toInputString(value: any, type: 'date' | 'month' | 'week' | 'time' | 'datetime-local'): String | string;
  }
};

extend();

export function extend() {
  // Returns the ISO 8601 week number (1-53)
  Date.prototype.getWeek = function(): Number | number {
    const d = new Date(this.getTime());
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return weekNo;
  };

  // Formatting extension https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toString
  Date.prototype.toStringFormat = function (
    format: String | string = 'YYYY-MM-DD HH:mm:ss',
    dayNames: Array<String | string> = ['Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota', 'Nedeľa']
  ): String | string {
    let lastChar: String | string = '';
    let formatPart: String | string = '';
    let formatParts: Array<String | string> = [];
    let ret: String | string = '';
    for (const fc of format) {
      if ((lastChar != '') && (lastChar != fc)) {
        formatParts.push(formatPart);
        formatPart = '';
      }
      formatPart += fc;
      lastChar = fc;
    }
    if (formatPart.length != 0)
      formatParts.push(formatPart);
    for (formatPart of formatParts) {
      let temp = '';
      switch (formatPart) {
        case 'YYYY':
          temp = this.getFullYear().toString();
          break;
        case 'YY':
          temp = (this.getFullYear() % 100).toString();
          break;
        case 'MM':
          temp = (this.getMonth() + 1).toString().padStart(2, '0');
          break;
        case 'M':
          temp = (this.getMonth() + 1).toString();
          break;
        case 'DD':
          temp = this.getDate().toString().padStart(2, '0');
          break;
        case 'D':
          temp = this.getDate().toString();
          break;
        case 'X': // day of week mon-sun
          if (dayNames in ((this.getDay() - 1) as number))
            temp = dayNames[(this.getDay() - 1)].toString();
          else
            throw 'Invalid day names';
          break;
        case 'x': // day of week 1-7
          let dow = this.getDay();
          if (dow == 0)
            dow = 7;
          temp = dow.toString();
          break;
        case 'HH':
          temp = this.getHours().toString().padStart(2, '0');
          break;
        case 'H':
          temp = this.getHours().toString();
          break;
        case 'mm':
          temp = this.getMinutes().toString().padStart(2, '0');
          break;
        case 'm':
          temp = this.getMinutes().toString();
          break;
        case 's':
          temp = this.getSeconds().toString();
          break;
        case 'ss':
          temp = this.getSeconds().toString().padStart(2, '0');
          break;
        case 'sss':
          temp = this.getMilliseconds().toString().padStart(3, '0');
          break;
        case 'ww':
          temp = this.getWeek().toString().padStart(2, '0');
          break;
        default:
          temp = formatPart.toString();
      }
      ret += temp;
    }
    return (ret as string);
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
  Date.prototype.toInputDateString = function (): String | string {
    return this.toStringFormat('YYYY-MM-DD');
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time
  Date.prototype.toInputTimeString = function (): String | string {
    return this.toStringFormat('HH:mm');
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/month
  Date.prototype.toInputMonthString = function (): String | string {
    return this.toStringFormat('YYYY-MM');
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/week
  Date.prototype.toInputWeekString = function (): String | string {
    return this.toStringFormat('YYYY-Www');
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local
  Date.prototype.toInputDatetimeLocalString = function (): String | string {
    return this.toStringFormat('YYYY-MM-DDTHH:mm');
  };

  Date.prototype.toInputString = function (
    type: 'date' | 'month' | 'week' | 'time' | 'datetime-local'
  ): String | string {
    switch (type) {
      case 'date':
        return this.toInputDateString();
      case 'time':
        return this.toInputTimeString();
      case 'month':
        return this.toInputMonthString();
      case 'week':
        return this.toInputWeekString();
      case 'datetime-local':
        return this.toInputDatetimeLocalString();
      default:
        return ''; // Return an empty string or handle other types as needed
    }
  };

  Date.isDate = function (
    value: any
  ): boolean {
    if (((value !== null) && (value !== undefined)) ? (String.isString(value) || (! isNaN(value))) : false)
      return (! isNaN((new Date(value as string | number)).getTime()));
    return (value instanceof Date);
  };

  // Simple funtion for parsing Date - value: null = now
  Date.parseData = function (
    value: any
  ): Date {
    if (((value !== null) && (value !== undefined)) ? (String.isString(value) || (! isNaN(value))) : false)
      return new Date(value as string | number);
    else if (value instanceof Date)
      return value as Date;
    return new Date();
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
  Date.toInputDateString = function (
    value: any
  ): String | string {
    return Date.parseData(value).toInputDateString();
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time
  Date.toInputTimeString = function (
    value: any
  ): String | string {
    return Date.parseData(value).toInputTimeString();
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/month
  Date.toInputMonthString = function (
    value: any
  ): String | string {
    return Date.parseData(value).toInputMonthString();
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/week
  Date.toInputWeekString = function (
    value: any
  ): String | string {
    return Date.parseData(value).toInputWeekString();
  };

  // Formatting for https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local
  Date.toInputDatetimeLocalString = function (
    value: any
  ): String | string {
    return Date.parseData(value).toInputDatetimeLocalString();
  };

  Date.toInputString = function (
    value: any,
    type: 'date' | 'month' | 'week' | 'time' | 'datetime-local'
  ): String | string {
    return Date.parseData(value).toInputString(type);
  };
};

export {}; // Keeps it a valid module
